import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Badge, InputGroup, FormControl, Tooltip, OverlayTrigger, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../services/api';
import { FiSearch, FiCalendar, FiClock, FiMapPin, FiInfo, FiArrowLeft } from 'react-icons/fi';
import FeedbackModal from '../components/FeedbackModal';
import ContextMenu from '../components/ContextMenu';
import TrainingFormModal from '../components/TrainingFormModal';

// Configuração do locale para Português
moment.locale('pt-br');
const localizer = momentLocalizer(moment);

// Função para gerar cores consistentes baseadas no ID ou nome
const generateColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 70%, 50%)`;
};

const TrainingCalendar = () => {
    const [rooms, setRooms] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [selectedRoomIds, setSelectedRoomIds] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ show: false, type: '', title: '', text: '' });
    
    // Context Menu & Modal States
    const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, data: null, type: null });
    const [modal, setModal] = useState({ show: false, mode: 'create', initialData: null });
    const [viewType, setViewType] = useState('month');

    // Carregar Salas e Treinamentos
    const fetchData = async () => {
        setLoading(true);
        try {
            const [roomsRes, trainingsRes] = await Promise.all([
                api.get('/rooms'),
                api.get('/trainings')
            ]);

            // Processar Salas
            const activeRooms = roomsRes.data.filter(r => r.ativa);
            const roomsWithColor = activeRooms.map(room => ({
                ...room,
                color: generateColor(room.nome + room.id)
            }));
            setRooms(roomsWithColor);
            
            // Selecionar todas as salas inicialmente se não houver seleção prévia
            if (selectedRoomIds.size === 0) {
                setSelectedRoomIds(new Set(roomsWithColor.map(r => r.id)));
            }

            // Processar Treinamentos
            const formattedTrainings = trainingsRes.data.map(t => {
                const start = new Date(t.data);
                const end = new Date(start.getTime() + (t.duracao * 60 * 60 * 1000));
                return {
                    ...t,
                    start,
                    end,
                    resourceId: t.sala_id
                };
            });
            setTrainings(formattedTrainings);

        } catch (error) {
            console.error("Erro ao carregar dados:", error);
            setFeedback({
                show: true,
                type: 'error',
                title: 'Erro',
                text: 'Não foi possível carregar os dados do calendário.'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle Context Menu
    const handleContextMenu = (e, value, type, eventData = null) => {
        // Verifica se e possui preventDefault (pode ser um objeto Touch que não possui)
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
            e.stopPropagation(); // Stop propagation to prevent double triggers
        }
        
        setContextMenu({
            show: true,
            x: e.clientX,
            y: e.clientY,
            data: value, // Date or Event
            type: type, // 'slot' or 'event'
            eventData: eventData
        });
    };

    const handleContextMenuAction = (action) => {
        setContextMenu({ ...contextMenu, show: false });
        
        if (action === 'create') {
            // Prepare initial data based on click context
            const date = contextMenu.type === 'event' ? contextMenu.eventData.start : contextMenu.data;
            const dateStr = moment(date).format('YYYY-MM-DD');
            
            // Only pre-fill time if not in month view
            const timeStr = viewType === 'month' ? '' : moment(date).format('HH:mm');

            setModal({
                show: true,
                mode: 'create',
                initialData: {
                    date: dateStr,
                    time: timeStr
                }
            });
        } else if (action === 'edit' && contextMenu.type === 'event') {
            const event = contextMenu.eventData;
            setModal({
                show: true,
                mode: 'edit',
                initialData: {
                    id: event.id,
                    titulo: event.titulo,
                    descricao: event.descricao,
                    date: moment(event.start).format('YYYY-MM-DD'),
                    time: moment(event.start).format('HH:mm'),
                    duracao: event.duracao,
                    sala_id: event.sala_id,
                    local: event.local,
                    link_online: event.link_online,
                    obriga_checkout: event.obriga_checkout
                }
            });
        }
    };

    // Custom Wrappers for Context Menu Support
    const DateCellWrapper = ({ value, children }) => {
        const touchTimer = React.useRef(null);

        const handleTouchStart = (e) => {
            e.persist();
            touchTimer.current = setTimeout(() => {
                handleContextMenu(e.touches[0], value, 'slot');
            }, 500);
        };

        const handleTouchEnd = () => {
            if (touchTimer.current) clearTimeout(touchTimer.current);
        };

        return (
            <div 
                className="rbc-day-bg"
                onContextMenu={(e) => handleContextMenu(e, value, 'slot')}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                {children}
            </div>
        );
    };

    const TimeSlotWrapper = ({ value, children }) => {
        // Similar implementation for time slots if needed, 
        // but rbc-time-slot doesn't wrap content same way.
        // For Week/Day view, we rely on `onSelectSlot` usually, 
        // but for context menu we can try attaching to the slot.
        return (
            <div 
                className="rbc-time-slot"
                onContextMenu={(e) => handleContextMenu(e, value, 'slot')}
            >
                {children}
            </div>
        );
    };

    // Manipular seleção de salas
    const toggleRoom = (roomId) => {
        const newSelected = new Set(selectedRoomIds);
        if (newSelected.has(roomId)) {
            newSelected.delete(roomId);
        } else {
            newSelected.add(roomId);
        }
        setSelectedRoomIds(newSelected);
    };

    // Filtrar eventos
    const filteredEvents = trainings.filter(event => {
        const matchesRoom = selectedRoomIds.has(event.sala_id);
        const matchesSearch = searchTerm === '' || 
            event.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (event.instrutor && event.instrutor.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesRoom && matchesSearch;
    });

    // Estilização dos eventos no calendário
    const eventStyleGetter = (event) => {
        const room = rooms.find(r => r.id === event.sala_id);
        const backgroundColor = room ? room.color : '#3174ad';
        return {
            style: {
                backgroundColor,
                borderRadius: '5px',
                opacity: 0.8,
                color: 'white',
                border: '0px',
                display: 'block'
            }
        };
    };

    // Componente Customizado para Tooltip
    const EventComponent = ({ event }) => {
        const touchTimer = React.useRef(null);

        const handleTouchStart = (e) => {
            e.persist();
            touchTimer.current = setTimeout(() => {
                handleContextMenu(e.touches[0], null, 'event', event);
            }, 500);
        };

        const handleTouchEnd = () => {
            if (touchTimer.current) clearTimeout(touchTimer.current);
        };

        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={`tooltip-${event.id}`}>
                        <div className="text-start">
                            <strong>{event.titulo}</strong><br/>
                            <FiClock className="me-1"/> {moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}<br/>
                            <FiMapPin className="me-1"/> {event.local}<br/>
                            {event.descricao && <><FiInfo className="me-1"/> {event.descricao}</>}
                        </div>
                    </Tooltip>
                }
            >
                <div 
                    className="p-1 h-100 d-flex align-items-center"
                    onContextMenu={(e) => handleContextMenu(e, null, 'event', event)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    style={{ pointerEvents: 'auto', cursor: 'context-menu' }}
                >
                    <div className="fw-bold text-truncate" style={{ fontSize: '0.85em' }}>{event.titulo}</div>
                </div>
            </OverlayTrigger>
        );
    };

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex align-items-center mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/dashboard" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Dashboard
                </Button>
            </div>              
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>
                    Calendário de Treinamentos
                </h2>
                <Button variant="primary" onClick={() => navigate('/create-training')}>
                    Novo Treinamento
                </Button>
            </div>
            <Row className="justify-content-center">
                {/* Sidebar de Salas (Topo no Mobile, Esquerda no Desktop) */}
                <Col lg={3} className="mb-4 mb-lg-0 order-1 order-lg-1">
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white border-bottom-0 pt-4 px-4">
                            <h5 className="fw-bold mb-0 text-uppercase text-muted small">Salas</h5>
                            <InputGroup className="mt-3">
                                <InputGroup.Text className="bg-light border-end-0">
                                    <FiSearch className="text-muted" />
                                </InputGroup.Text>
                                <FormControl 
                                    placeholder="Buscar treinamento..." 
                                    className="bg-light border-start-0 shadow-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Card.Header>
                        <Card.Body className="px-4 overflow-auto" style={{ maxHeight: '600px' }}>
                            <Form>
                                {rooms.map(room => (
                                    <div key={room.id} className="d-flex align-items-center mb-3 p-2 rounded hover-bg-light transition-all">
                                        <Form.Check 
                                            type="checkbox"
                                            id={`room-${room.id}`}
                                            checked={selectedRoomIds.has(room.id)}
                                            onChange={() => toggleRoom(room.id)}
                                            label=""
                                            className="me-2 shadow-none"
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <div 
                                            className="rounded-circle me-2 shadow-sm" 
                                            style={{ width: '12px', height: '12px', backgroundColor: room.color }}
                                        />
                                        <label htmlFor={`room-${room.id}`} className="flex-grow-1 cursor-pointer m-0 text-truncate user-select-none small" style={{ fontSize: '0.9rem' }}>
                                            {room.nome}
                                        </label>
                                        <Badge bg="light" text="dark" className="ms-auto border rounded-pill">
                                            {trainings.filter(t => t.sala_id === room.id).length}
                                        </Badge>
                                    </div>
                                ))}
                                {rooms.length === 0 && !loading && (
                                    <p className="text-muted text-center py-4 small">Nenhuma sala encontrada.</p>
                                )}
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Calendário */}
                <Col lg={9} className="order-2 order-lg-2">
                    <Card className="shadow-sm border-0 calendar-card-height">
                        <Card.Body className="p-3 h-100">
                            <Calendar
                                localizer={localizer}
                                events={filteredEvents}
                                startAccessor="start"
                                endAccessor="end"
                                style={{ height: '100%' }}
                                eventPropGetter={eventStyleGetter}
                                components={{
                                    event: EventComponent,
                                    dateCellWrapper: DateCellWrapper,
                                    timeSlotWrapper: TimeSlotWrapper
                                }}
                                formats={{
                                    eventTimeRangeFormat: () => null, // Remove hora do título
                                }}
                                messages={{
                                    next: "Próximo",
                                    previous: "Anterior",
                                    today: "Hoje",
                                    month: "Mês",
                                    week: "Semana",
                                    day: "Dia",
                                    agenda: "Agenda",
                                    date: "Data",
                                    time: "Hora",
                                    event: "Evento",
                                    noEventsInRange: "Não há eventos neste período."
                                }}
                                views={['month', 'week', 'day', 'agenda']}
                                defaultView='month'
                                onView={(view) => setViewType(view)}
                                popup
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <ContextMenu 
                show={contextMenu.show}
                x={contextMenu.x}
                y={contextMenu.y}
                onClose={() => setContextMenu({ ...contextMenu, show: false })}
                onAction={handleContextMenuAction}
                training={contextMenu.type === 'event'}
            />

            <TrainingFormModal 
                show={modal.show}
                onHide={() => setModal({ ...modal, show: false })}
                initialData={modal.initialData}
                mode={modal.mode}
                onSuccess={fetchData}
                rooms={rooms}
            />

            <FeedbackModal 
                show={feedback.show}
                onHide={() => setFeedback({ ...feedback, show: false })}
                type={feedback.type}
                title={feedback.title}
                message={feedback.text}
            />
            
            <style jsx>{`
                /* Ajuste de altura do calendário para evitar overflow e garantir scroll interno */
                .calendar-card-height {
                    height: 600px; /* Altura fixa menor */
                    overflow: hidden; /* Garante que o scroll seja interno ao componente Calendar */
                }

                @media (max-width: 991px) {
                    /* Em mobile, o calendário tem altura fixa ou menor pois o sidebar está em cima */
                    .calendar-card-height {
                        height: 500px;
                        margin-bottom: 20px;
                    }
                    
                    .rbc-toolbar {
                        flex-direction: column;
                        gap: 10px;
                    }
                    .rbc-btn-group {
                        width: 100%;
                        display: flex;
                        justify-content: center;
                    }
                }

                .hover-bg-light:hover {
                    background-color: #f8f9fa;
                }
                .cursor-pointer {
                    cursor: pointer;
                }
                .rbc-calendar {
                    font-family: 'Inter', sans-serif;
                }
                .rbc-header {
                    padding: 12px 0;
                    font-weight: 600;
                    color: var(--color-text-main);
                }
                .rbc-month-view {
                    border: none;
                }
                .rbc-day-bg + .rbc-day-bg {
                    border-left: 1px solid #f0f0f0;
                }
                .rbc-month-row + .rbc-month-row {
                    border-top: 1px solid #f0f0f0;
                }
                .rbc-today {
                    background-color: rgba(0, 191, 165, 0.05);
                }
                .rbc-off-range-bg {
                    background-color: #fcfcfc;
                }
            `}</style>
        </Container>
    );
};

export default TrainingCalendar;
